import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_backward.svg?react';
import { useTranslation } from 'react-i18next';
import PlayIcon from '@jwp/ott-theme/assets/icons/play.svg?react';
import PauseIcon from '@jwp/ott-theme/assets/icons/pause.svg?react';
import ExpandIcon from '@jwp/ott-theme/assets/icons/expand.svg?react';
import MinimizeIcon from '@jwp/ott-theme/assets/icons/minimize.svg?react';
import VolumeUpIcon from '@jwp/ott-theme/assets/icons/audio-on.svg?react';
import VolumeOffIcon from '@jwp/ott-theme/assets/icons/audio-off.svg?react';
import DefaultAvatar from '@jwp/ott-theme/assets/profiles/avatar.svg';

import Icon from '../Icon/Icon';
import IconButton from '../IconButton/IconButton';
import ProfileCircle from '../ProfileCircle/ProfileCircle';
import { formatTime } from '../../utils/formatTime';
import Spinner from '../Spinner/Spinner';

import styles from './VideoPreview.module.scss';

type Props = {
  file?: File | null;
  previewUrl?: string | null;
  poster?: string;
  onClose: () => void;
  title?: string;
  description?: string;
  category?: string;
  author?: {
    name: string | null;
    avatarUrl: string | null;
  };
  showDetails?: boolean;
  isReel?: boolean;
};

const VideoPreview = ({ file, previewUrl, poster, onClose, title, description, category, author, showDetails = true, isReel = true }: Props) => {
  const { t } = useTranslation('upload');
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [volume, setVolume] = useState(1);
  const [showVolumeBar, setShowVolumeBar] = useState(false);

  useEffect(() => {
    if (previewUrl) {
      setVideoUrl(previewUrl);
      return;
    }
    if (file) {
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setVideoUrl(null);
  }, [file, previewUrl]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
      if (!newMuted && volume === 0) {
        setVolume(1);
        videoRef.current.volume = 1;
      }
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    } else {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
      setIsFullscreen(true);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };
  
  const handleWaiting = () => setIsLoading(true);
  const handlePlaying = () => setIsLoading(false);
  const handleCanPlay = () => setIsLoading(false);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number.parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number.parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      if (val > 0 && isMuted) {
        videoRef.current.muted = false;
        setIsMuted(false);
      } else if (val === 0 && !isMuted) {
        videoRef.current.muted = true;
        setIsMuted(true);
      }
    }
  };

  return (
    <div className={styles.previewOverlay} ref={containerRef}>
      <div className={styles.header} style={{ alignItems: showDetails ? 'flex-start' : 'center' }}>
        <IconButton
          onClick={(e) => {
            e?.stopPropagation();
            e?.preventDefault();
            onClose();
          }}
          className={styles.backBtn}
        >
          <Icon icon={ArrowLeft} />
        </IconButton>
        <div className={styles.headerContent}>
          {title && <h2 className={styles.previewTitle}>{title}</h2>}
          <div className={styles.metaContainer}>
            {showDetails && isReel && description && <span className={styles.description}>{description}</span>}
            {showDetails && isReel && category && <span className={classNames(styles.categoryBadge, styles.mobileCategory)}>{category}</span>}
            {showDetails && isReel && author?.name && (
              <div className={styles.authorContainer}>
                <ProfileCircle src={author?.avatarUrl || DefaultAvatar} alt={author.name} className={styles.authorAvatar} />
                <span className={styles.authorName}>{author.name}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.centerContainer}>
        <div
          className={classNames(styles.videoWrapper, { [styles.horizontalWrapper]: !isReel })}
          onClick={togglePlay}
          onKeyDown={(e) => e.key === ' ' && togglePlay()}
          aria-label={t('upload_box.preview_alt')}
        >
          {videoUrl && (
            <video
              ref={videoRef}
              src={videoUrl}
              className={styles.video}
              poster={poster}
              muted={isMuted}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleEnded}
              onWaiting={handleWaiting}
              onPlaying={handlePlaying}
              onCanPlay={handleCanPlay}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              autoPlay
              playsInline
            >
              <track kind="captions" />
            </video>
          )}

          {!isPlaying && isReel && !isLoading && (
            <div className={styles.playOverlay}>
              <Icon icon={PlayIcon} className={styles.largePlayIcon} />
            </div>
          )}

          {isLoading && (
            <div className={styles.loadingOverlay}>
              <Spinner size="medium" />
            </div>
          )}

          {isReel && !isLoading && (
            <div 
              className={styles.topRightControls}
              onMouseEnter={() => setShowVolumeBar(true)}
              onMouseLeave={() => setShowVolumeBar(false)}
              onClick={(e) => e.stopPropagation()}
            >
              {showVolumeBar && (
                <div className={styles.volumeBarContainer} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className={styles.volumeSlider}
                    style={{ '--percent': `${(isMuted ? 0 : volume) * 100}%` } as React.CSSProperties}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              <IconButton
                onClick={(e) => {
                  e?.stopPropagation();
                  toggleMute();
                }}
                className={styles.muteBtn}
              >
                <Icon icon={isMuted || volume === 0 ? VolumeOffIcon : VolumeUpIcon} />
              </IconButton>
            </div>
          )}

          {showDetails && isReel && category && !isLoading && (
            <div className={styles.topLeftControls}>
              <span className={styles.categoryBadge}>{category}</span>
            </div>
          )}

          {!isLoading && (
            <div className={styles.bottomControls} onClick={(e) => e.stopPropagation()}>
              <div className={styles.progressBarContainer}>
                <input
                  type="range"
                  className={styles.scrubber}
                  style={{ '--percent': `${(currentTime / (duration || 1)) * 100}%` } as React.CSSProperties}
                  min={0}
                  max={duration || 0}
                  step={0.1}
                  value={currentTime}
                  onChange={handleSeek}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {!isReel && (
                <div className={styles.controlsRow}>
                  <div className={styles.leftControls}>
                    <IconButton
                      onClick={(e) => {
                        e?.stopPropagation();
                        togglePlay();
                      }}
                      className={classNames(styles.controlBtn, { [styles.whiteControl]: !isReel })}
                    >
                      {isPlaying ? (
                        <Icon icon={PauseIcon} />
                      ) : (
                        <Icon icon={PlayIcon} />
                      )}
                    </IconButton>
                    <div 
                      className={styles.volumeControlWrapper}
                      onMouseEnter={() => setShowVolumeBar(true)}
                      onMouseLeave={() => setShowVolumeBar(false)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {showVolumeBar && (
                        <div className={styles.volumeBarContainerHorizontal} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={isMuted ? 0 : volume}
                            onChange={handleVolumeChange}
                            className={styles.volumeSlider}
                            style={{ '--percent': `${(isMuted ? 0 : volume) * 100}%` } as React.CSSProperties}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                      <IconButton
                        onClick={(e) => {
                          e?.stopPropagation();
                          toggleMute();
                        }}
                        className={styles.controlBtn}
                      >
                        <Icon icon={isMuted || volume === 0 ? VolumeOffIcon : VolumeUpIcon} />
                      </IconButton>
                    </div>
                    <div className={styles.timeInfo}>
                      <span>{formatTime(Math.floor(currentTime))}</span>
                      <span className={styles.timeDivider}>/</span>
                      <span>{formatTime(Math.floor(duration))}</span>
                    </div>
                  </div>
                  <div className={styles.rightControls}>
                    <IconButton
                      onClick={(e) => {
                        e?.stopPropagation();
                        toggleFullscreen();
                      }}
                      className={styles.controlBtn}
                    >
                      {isFullscreen ? (
                        <Icon icon={MinimizeIcon} />
                      ) : (
                        <Icon icon={ExpandIcon} />
                      )}
                    </IconButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default VideoPreview;
